/**
 * Cast / AirPlay / Remote Playback helper.
 *
 * Strategy (matches the user's request "möglichst überall, auch im Hotel"):
 *  1. **Safari (iOS/macOS)**: skip the Cast SDK entirely. Native HLS playback
 *     on the video tag exposes the AirPlay button automatically — including
 *     hotel-paired AirPlay TVs (LG Pro:Centric / Samsung Hospitality / etc.)
 *     after QR-pairing.
 *  2. **Chrome/Edge desktop + Android**: lazy-load Google CAF v3 sender SDK
 *     and expose a `<google-cast-launcher>` button. Default receiver app ID
 *     is `CC1AD845` (built-in Default Media Receiver) which handles HLS/MP4
 *     out of the box; users who register a custom receiver can override
 *     via `window.ZAP_CAST_APP_ID = '...';` in the shell.
 *  3. **Generic fallback**: Remote Playback API (`video.disableRemotePlayback
 *     = false`) — browsers that support it (Chrome) show their own cast
 *     button on the video element.
 *  4. **Universal**: "Stream-URL kopieren" — copies a playable URL the user
 *     can paste into mpv / VLC / a TV's "Media Player → URL" feature.
 */

import { COPY_RESET_MS } from './timing.js';

const APP_ID = window.ZAP_CAST_APP_ID || 'CC1AD845';   // default Media Receiver

let initPromise = null;
let castCtx = null;
let lastState = 'NO_DEVICES_AVAILABLE';
const listeners = new Set();

function isSafari() {
  const ua = navigator.userAgent;
  if (/Edg|Chrome|Chromium|OPR|FxiOS|CriOS/.test(ua)) return false;
  return /Safari/.test(ua) || /iPhone|iPad|iPod/.test(ua);
}

/** Returns true if the Cast SDK can run in this browser (= not Safari). */
export function castSupported() {
  return !isSafari();
}

/** Initialize the SDK exactly once. Resolves to the CastContext or null. */
export function castInit() {
  if (initPromise) return initPromise;
  if (!castSupported()) {
    initPromise = Promise.resolve(null);
    return initPromise;
  }
  initPromise = new Promise((resolve) => {
    window['__onGCastApiAvailable'] = (ok) => {
      if (!ok || !window.cast?.framework) { resolve(null); return; }
      try {
        const ctx = window.cast.framework.CastContext.getInstance();
        ctx.setOptions({
          receiverApplicationId: APP_ID,
          autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        });
        ctx.addEventListener(
          window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
          (ev) => { lastState = ev.castState; listeners.forEach(fn => { try { fn(ev.castState); } catch {} }); }
        );
        castCtx = ctx;
        resolve(ctx);
      } catch (e) {
        resolve(null);
      }
    };
    const s = document.createElement('script');
    s.src = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
    s.async = true;
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
  return initPromise;
}

/** Subscribe to availability changes. Returns an unsubscribe fn. */
export function castOnState(cb) {
  listeners.add(cb);
  cb(lastState);
  return () => listeners.delete(cb);
}

/** Cast a media URL on the active session (or open device picker if none). */
export async function castMedia({ url, contentType, title, poster, currentTime = 0 }) {
  await castInit();
  if (!castCtx) throw new Error('cast unavailable');
  let session = castCtx.getCurrentSession();
  if (!session) {
    try { await castCtx.requestSession(); } catch { throw new Error('no session'); }
    session = castCtx.getCurrentSession();
  }
  if (!session) throw new Error('no session after request');
  const mi = new window.chrome.cast.media.MediaInfo(url, contentType || 'application/x-mpegURL');
  const md = new window.chrome.cast.media.GenericMediaMetadata();
  if (title)  md.title  = title;
  if (poster) md.images = [new window.chrome.cast.Image(poster)];
  mi.metadata = md;
  const req = new window.chrome.cast.media.LoadRequest(mi);
  req.currentTime = currentTime;
  return session.loadMedia(req);
}

/**
 * Wire up cast/airplay/stream-url buttons inside a player overlay.
 *
 * Markup expected (build via JS or HTML; we don't impose):
 *   <button class="po-btn" data-cast-launcher>Cast</button>     (Chrome only)
 *   <button class="po-btn" data-cast-copyurl>Stream-URL</button> (universal)
 * Non-Safari browsers also get the Remote Playback API enabled on <video>
 * automatically (`disableRemotePlayback = false`).
 */
export function attachCastButtons(overlay, opts) {
  const { mediaUrl, contentType, title, poster, getCurrentTime } = opts || {};
  if (!overlay) return;

  const video = overlay.querySelector('video');
  if (video) video.disableRemotePlayback = false;

  const launcher = overlay.querySelector('[data-cast-launcher]');
  const copyBtn  = overlay.querySelector('[data-cast-copyurl]');

  // Cast launcher (Chrome/Edge/Android — Safari uses native AirPlay on the
  // <video> element instead, so we hide the launcher there).
  // Visibility model (changed 2026-05-04): the button stays visible whenever
  // Cast is supported by the browser, but gets `.is-unavailable` when no
  // device is advertising. That way the user always sees the affordance and
  // can click to open the Chrome device-picker even before mDNS has found a
  // receiver — instead of staring at an empty bar wondering where Cast went.
  if (launcher) {
    if (!castSupported()) {
      launcher.hidden = true;
    } else {
      launcher.hidden = false;
      const apply = (state) => {
        const usable = (state === 'CAST_AVAILABLE'
                     || state === 'CONNECTED'
                     || state === 'NOT_CONNECTED');
        launcher.classList.toggle('is-unavailable', !usable);
        launcher.classList.toggle('is-on', state === 'CONNECTED');
      };
      // Apply current state immediately (before SDK loads, this is
      // 'NO_DEVICES_AVAILABLE' → shows as `is-unavailable` / dimmed).
      apply(lastState);
      castInit().then((ctx) => {
        if (!ctx) {
          launcher.classList.add('is-unavailable');
          return;
        }
        const off = castOnState(apply);
        // Cleanup when overlay leaves DOM
        const obs = new MutationObserver(() => {
          if (!document.body.contains(overlay)) { off(); obs.disconnect(); }
        });
        obs.observe(document.body, { childList: true });
      });
      launcher.addEventListener('click', async () => {
        try {
          await castMedia({
            url: mediaUrl, contentType, title, poster,
            currentTime: getCurrentTime ? (getCurrentTime() || 0) : 0,
          });
        } catch (e) {
          console.warn('cast failed', e);
        }
      });
    }
  }

  // Universal "Stream-URL kopieren" → clipboard.
  if (copyBtn && mediaUrl) {
    const fullUrl = mediaUrl.startsWith('http') ? mediaUrl : (window.location.origin + mediaUrl);
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(fullUrl);
        copyBtn.classList.add('is-on');
        setTimeout(() => copyBtn.classList.remove('is-on'), COPY_RESET_MS);
      } catch {
        // clipboard blocked — fall back to selection
        const t = document.createElement('input');
        t.value = fullUrl; t.style.position = 'fixed'; t.style.left = '-9999px';
        document.body.appendChild(t); t.select();
        try { document.execCommand('copy'); } catch {}
        t.remove();
      }
    });
  }
}

/**
 * For an HLS player using MSE/hls.js (non-Safari), browsers that DO support
 * AirPlay (desktop Safari with hls.js) can be made to show the AirPlay
 * button by appending a "alternative source" <source> element pointing at
 * the m3u8 URL. WebKit blog 15036.
 */
export function appendAirplayAlternativeSource(video, hlsUrl) {
  if (!video || !hlsUrl) return;
  if (video.querySelector('source[data-airplay-alt]')) return;
  const s = document.createElement('source');
  s.setAttribute('data-airplay-alt', '1');
  s.src = hlsUrl;
  s.type = 'application/vnd.apple.mpegurl';
  video.appendChild(s);
}
